import { useState, useCallback, useEffect } from "react";

const LS_KEY_SMS = "inbox_toast_sms_enabled";
const LS_KEY_VM  = "inbox_toast_vm_enabled";
const CUSTOM_EVENT_SMS = "inbox-toast-sms-pref-changed";
const CUSTOM_EVENT_VM  = "inbox-toast-vm-pref-changed";

function readBool(key: string, fallback = true): boolean {
  const v = localStorage.getItem(key);
  if (v === null) return fallback;
  return v !== "false";
}

export function useInboxToastNotifications() {
  const [smsEnabled, setSmsEnabled] = useState(() => readBool(LS_KEY_SMS));
  const [vmEnabled,  setVmEnabled]  = useState(() => readBool(LS_KEY_VM));

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_KEY_SMS) setSmsEnabled(e.newValue !== "false");
      if (e.key === LS_KEY_VM)  setVmEnabled(e.newValue !== "false");
    };
    const onSms = (e: Event) => setSmsEnabled((e as CustomEvent<boolean>).detail);
    const onVm  = (e: Event) => setVmEnabled((e as CustomEvent<boolean>).detail);

    window.addEventListener("storage", onStorage);
    window.addEventListener(CUSTOM_EVENT_SMS, onSms);
    window.addEventListener(CUSTOM_EVENT_VM,  onVm);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(CUSTOM_EVENT_SMS, onSms);
      window.removeEventListener(CUSTOM_EVENT_VM,  onVm);
    };
  }, []);

  const toggleSms = useCallback(() => {
    setSmsEnabled((prev) => {
      const next = !prev;
      localStorage.setItem(LS_KEY_SMS, String(next));
      window.dispatchEvent(new CustomEvent(CUSTOM_EVENT_SMS, { detail: next }));
      return next;
    });
  }, []);

  const toggleVm = useCallback(() => {
    setVmEnabled((prev) => {
      const next = !prev;
      localStorage.setItem(LS_KEY_VM, String(next));
      window.dispatchEvent(new CustomEvent(CUSTOM_EVENT_VM, { detail: next }));
      return next;
    });
  }, []);

  // Legacy compat — treated as "either channel is on"
  const enabled = smsEnabled || vmEnabled;

  return { smsEnabled, vmEnabled, toggleSms, toggleVm, enabled };
}
